<template>
  <b-container v-if="initialDataFetched" fluid>
    <b-row v-if="!noSessions">
      <b-col>
        <header-filter />
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <div v-if="!noData && !isSession && timelineOnScreen" class="section">
          <div class="section-content">
            <div class="dashboard-statistics">
              <donut-graphs />
            </div>
            <div class="statistic-border" :style="{'border': `1px solid ${metricColor}`}"></div>

            <div class="stats-table">
              <stats-header :current-metric="currentMetric" />

            <div class="display-inline-block timeline-pagination">
               <button class="btn btn-transparent-bkg ng-isolate-scope pagination-btn" :disabled="timelinePage===0" @click="goToPreviousPage"><img src="@/assets/images/icons/arrow-left-icon.png" alt="left"></button>
               <h4 class="header-label text-white display-inline-block">
                  <span>{{timelinePage+1 + ' / ' + timelineTotalPages}}</span>
               </h4>
               <button class="btn btn-transparent-bkg btn-next ng-isolate-scope pagination-btn" :disabled="timelinePage===timelineTotalPages-1" @click="goToNextPage"><img src="@/assets/images/icons/arrow-left-icon.png" alt="right"></button>
             </div>

              <div class="stats-chart">
                <line-chart
                  v-if="timelineOnScreen"
                  :player="account"
                  :selected-team="selectedTeam"
                  :color="metricColor"
                  :data="timelineValuesOnScreen"
                  :height="400"
                  :y-ticks="getYTicks()"
                  :y-unit="yUnit()"
                  :y-axis-title="yAxisTitle()"
                  :right-y-label-axis="rightYLabelAxis()"
                  :teams="teams"
                  :minValue="timelineMinValue"
                  :maxValue="timelineMaxValue"
                  :itemsPerPage="timelineItemsPerPage"
                />
              </div>
            </div>
          </div>
        </div>

        <calendar-activity-container :dashboardLastActivityData="lastSessionData" :isDashboard="true" v-if="!noSessions && isSession"/>

        <welcome v-if="noSessions" />

        <div v-if="noData && !noSessions && !isSession" class="section">
          <div class="section-content with-padding-top">
            <div class="dashboard-statistics">
              <div :class="`page-info-assistant ${displayError ? 'loaded' : ''}`">
                <div class="bubble-wrapper">
                  <div class="message-text">
                    <div class="table-wrapper">
                      <div class="cell">{{ $t('DASHBOARD.noDataFound')}}</div>
                    </div>
                  </div>
                </div>
                <div class="no-data-img-wrapper">
                  <img src="@/assets/images/assistant-info/assistant.png" alt="assistant" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import moment from "moment";
import { TOASTER_ERROR } from "@/common/events.types";
import { mapGetters, mapActions, mapMutations } from "vuex";
import {
  FETCH_DASHBOARD_DATA,
  RESET_DASHBOARD_STATE,
  UPDATE_INITIAL_DATA_FETCHED
} from "@/store/action.types";
import { GO_TO_NEXT_PAGE, GO_TO_PREVIOUS_PAGE } from '@/store/mutation.types'
import DonutGraphs from "@/components/dashboard/DonutGraphsContainer";
import LineChart from "@/components/charts/LineChart";
import CalendarActivityContainer from "./CalendarActivityContainer.vue";
import HeaderFilter from "@/components/dashboard/HeaderFilterContainer";
import { METRIC_COLOR, METRICS, TIME_RANGE } from "@/constants/DashboardConstants";
import { SESSION_METRICS } from "@/constants/SessionConsts";
import StatsHeader from "@/components/dashboard/StatsHeader";
import STATUS_CODES from "@/common/statusCodes";
import { openSupportChat } from "@/common/supportChat";
// Lazy load components
const Welcome = () => import('@/components/dashboard/Welcome');

export default {
  name: "Dashboard",
  mounted() {
    this.fetchDashboardDataActions(this.account.playerId).catch(
      ({ status }) => {
        if (status.code !== STATUS_CODES.BP_404) {
          this.$eventHub.$emit(TOASTER_ERROR, {
            message: this.$t("TOAST_MESSAGES.errorTryAgain")
          });
        }
      }
    );
    // The mobile apps link here with ?support to open the chat
    if ("support" in this.$route.query) {
      openSupportChat();
    }
  },
  data() {
    return {
      displayError: false,
    };
  },
  computed: {
    ...mapGetters([
      "account",
      "selectedTeam",
      "selectedEntity",
      "timelineOnScreen",
      "currentMetric",
      "teams",
      "noData",
      "noSessions",
      "initialDataFetched",
      "timelinePage",
      "timelineItemsPerPage",
      "timelineTotalPages",
      "timelineMinValue",
      "timelineMaxValue",
      "selectedTimeRange",
      "lastSessionData"
    ]),
    metricColor() {
      return METRIC_COLOR[this.currentMetric];
    },
    timelineValuesOnScreen(){
       return this.timelineOnScreen.slice(this.timelinePage*this.timelineItemsPerPage,this.timelinePage*this.timelineItemsPerPage+this.timelineItemsPerPage);
    },
    isSession(){
      return this.selectedTimeRange.value===TIME_RANGE.SESSION.value;
    }
  },
  destroyed() {
    this.resetStateAction();
  },

  methods: {
    ...mapActions({
      fetchDashboardDataActions: FETCH_DASHBOARD_DATA,
      resetStateAction: RESET_DASHBOARD_STATE,
      updateInitialDataFetchedFlagAction: UPDATE_INITIAL_DATA_FETCHED
    }),
    ...mapMutations({
      goToNextPage: GO_TO_NEXT_PAGE,
      goToPreviousPage: GO_TO_PREVIOUS_PAGE
    }),
    getLast10Days() {
      return [...new Array(10)]
        .map((i, idx) =>
          moment()
            .startOf("day")
            .subtract(idx + 1, "days")
            .toDate()
        )
        .reverse();
    },
    getYTicks() {
      let fixedYTicks;
      switch (this.currentMetric) {
        case METRICS.TIME_ABOVE_80_PERCENT_HR:
          fixedYTicks = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45];

          if (this.timelineMaxValue > fixedYTicks[fixedYTicks.length - 1]) {
            while (fixedYTicks[fixedYTicks.length - 1] < this.timelineMaxValue) {
              fixedYTicks.push(fixedYTicks[fixedYTicks.length - 1] + 5);
            }
          }
          return fixedYTicks;
        case METRICS.AP_PERCENTAGE:
        case SESSION_METRICS.AP:
          fixedYTicks = [0, 10, 20, 30, 40, 50, 60, 70, 80];

          if (this.timelineMaxValue > fixedYTicks[fixedYTicks.length - 1]) {
            while (fixedYTicks[fixedYTicks.length - 1] < this.timelineMaxValue) {
              fixedYTicks.push(fixedYTicks[fixedYTicks.length - 1] + 10);
            }
          }

          return fixedYTicks;
        case METRICS.DISTANCE:
          fixedYTicks = [
            0,
            1250,
            2500,
            3750,
            5000,
            6250,
            7500,
            8750,
            10000,
            11250
          ];

          if (this.timelineMaxValue > fixedYTicks[fixedYTicks.length - 1]) {
            while (fixedYTicks[fixedYTicks.length - 1] < this.timelineMaxValue) {
              fixedYTicks.push(fixedYTicks[fixedYTicks.length - 1] + 1250);
            }
          }

          return fixedYTicks;
        case METRICS.WORKLOAD:
          fixedYTicks = [0, 5, 10, 15, 20, 25, 30];

          if (this.timelineMaxValue > fixedYTicks[fixedYTicks.length - 1]) {
            while (fixedYTicks[fixedYTicks.length - 1] < this.timelineMaxValue) {
              fixedYTicks.push(fixedYTicks[fixedYTicks.length - 1] + 5);
            }
          }

          return fixedYTicks;
      }
    },
    yAxisTitle() {
      switch (this.currentMetric) {
        case METRICS.TIME_ABOVE_80_PERCENT_HR:
          return this.$t("DASHBOARD.lineChart.yAxisTitle.timeAbove80");
        case METRICS.AP_PERCENTAGE:
        case SESSION_METRICS.AP:
          return this.$t("DASHBOARD.lineChart.yAxisTitle.activeParticipation");
        case METRICS.DISTANCE:
          return this.account.unitDistance === UNIT_SYSTEM.IMPERIAL ? this.$t("DASHBOARD.lineChart.yAxisTitle.distanceImperial") : this.$t("DASHBOARD.lineChart.yAxisTitle.distanceMetric");
        case METRICS.WORKLOAD:
          return this.$t("DASHBOARD.lineChart.yAxisTitle.workload");
      }
    },
    rightYLabelAxis() {
      switch (this.currentMetric) {
        case METRICS.TIME_ABOVE_80_PERCENT_HR:
          return {
            array: [0, 7.5, 15, 22.5, 30, 37.5],
            labels: {
              "0": "",
              "7.5": this.$t("DASHBOARD.lineChart.rightYLabelAxis.low"),
              "15": "",
              "22.5": this.$t("DASHBOARD.lineChart.rightYLabelAxis.moderate"),
              "30": "",
              "37.5": this.$t("DASHBOARD.lineChart.rightYLabelAxis.high"),
              "45": ""
            }
          };
        case METRICS.AP_PERCENTAGE:
        case SESSION_METRICS.AP:
          return {
            array: [0, 20, 40, 50, 60, 70, 80],
            labels: {
              "0": "",
              "20": this.$t("DASHBOARD.lineChart.rightYLabelAxis.low"),
              "40": "",
              "50": this.$t("DASHBOARD.lineChart.rightYLabelAxis.moderate"),
              "60": "",
              "70": this.$t("DASHBOARD.lineChart.rightYLabelAxis.high"),
              "80": ""
            }
          };
        case METRICS.DISTANCE:
          return {
            array: [0, 1250, 2500, 3750, 5000, 7500],
            labels: {
              "0": "",
              "1250": this.$t("DASHBOARD.lineChart.rightYLabelAxis.low"),
              "2500": "",
              "3750": this.$t("DASHBOARD.lineChart.rightYLabelAxis.moderate"),
              "5000": "",
              "7500": this.$t("DASHBOARD.lineChart.rightYLabelAxis.high")
            }
          };
        case METRICS.WORKLOAD:
          return {
            array: [0, 5, 10, 15, 20, 25],
            labels: {
              "0": "",
              "5": this.$t("DASHBOARD.lineChart.rightYLabelAxis.low"),
              "10": "",
              "15": this.$t("DASHBOARD.lineChart.rightYLabelAxis.moderate"),
              "20": "",
              "25": this.$t("DASHBOARD.lineChart.rightYLabelAxis.high")
            }
          };
      }
    },
    yUnit() {
      switch (this.currentMetric) {
        case METRICS.TIME_ABOVE_80_PERCENT_HR:
          return "%";
        case METRICS.AP_PERCENTAGE:
        case SESSION_METRICS.AP:
          return "%";
        case METRICS.DISTANCE:
          return ' ' + getUnitLabel(this.account.unitDistance);
        case METRICS.WORKLOAD:
          return "%";
      }
    }
  },
  watch: {
    noData(v) {
      const context = this;

      if (v) {
        setTimeout(() => {
          context.displayError = true;
        }, 200);
      }
    },
    account(newAccount) {
      this.updateInitialDataFetchedFlagAction(false);
      this.resetStateAction();
      this.fetchDashboardDataActions(newAccount.playerId);
    }
  },
  components: {
    LineChart,
    CalendarActivityContainer,
    HeaderFilter,
    DonutGraphs,
    StatsHeader,
    Welcome
  }
};
</script>

<style lang="scss" scoped>
.dashboard-statistics {
  padding-top: 40px;
  margin-bottom: 40px;
}

 .stats-table{
     display: flex;
   flex-direction: column;
   .timeline-pagination{
      margin-top: 30px;
      align-self: center;
   }

   .pagination-btn:disabled{
      cursor: not-allowed;
   }

  }

.page-info-assistant {
  overflow: hidden;

  .no-data-img-wrapper {
    max-width: 600px;
    margin: 0 auto;
    position: relative;
    bottom: -800px;
    transition: all 0.5s;

    img {
      width: 100%;
    }
  }

  .bubble-wrapper {
    max-width: 500px;
    margin: 0 auto;
    position: relative;
    background-image: url("./../../assets/images/assistant-info/bubble-1.png");
    background-size: 100% 100%;
    top: -500px;
    transition: top 0.5s;
    right: -330px;

    .message-text {
      position: relative;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      margin: auto;
      color: $cod-gray;
      font-size: 18px;
      min-height: 196px;

      .table-wrapper {
        display: table;
        width: 100%;
        min-height: 190px;

        .cell {
          display: table-cell;
          vertical-align: middle;
          text-align: center;
          padding: 56px;
          padding-bottom: 80px;
        }
      }
    }

    img {
      max-width: 100%;
      height: calc(100% + 40px);
      position: absolute;
    }
  }

  &.loaded {
    .bubble-wrapper {
      top: 0;
      transition: top 0.5s;
    }

    .no-data-img-wrapper {
      bottom: 0;
      transition: bottom 0.5s;
    }
  }

  @media screen and (max-width: 1680px) {
    .bubble-wrapper {
      right: -180px;
    }

    .no-data-img-wrapper {
      max-width: 500px;
      left: -150px;
    }
  }

  @media screen and (max-width: 1200px) {
    .bubble-wrapper {
      right: -110px;
    }

    .no-data-img-wrapper {
      max-width: 450px;
      left: 0;
      margin: 0;
    }
  }

  @media screen and (max-width: 992px) {
    .bubble-wrapper {
      right: 0;
      margin: 0 auto;
      background-image: url("./../../assets/images/assistant-info/bubble-2.png");
      margin-bottom: 40px;

      .message-text .table-wrapper .cell {
        padding: 40px;
        padding-bottom: 80px;
      }
    }

    .no-data-img-wrapper {
      max-width: 450px;
      left: 0;
      margin: 0 auto;
    }
  }
}
</style>
